import React, { useCallback, useEffect, useRef, useState } from 'react';
import apiService from '../services/api';
import {
  AdminPayment,
  AdminPaymentListQuery,
  AdminRefundOutcome,
  AdminRefundResult,
  ApiError,
  PaymentStatus,
  PaymentTransaction,
} from '../types/api';
import { formatPaise, RefundTarget, RefundTargetState, refundTargets } from '../utils/paymentRefunds';

// The admin refund surface (issue #41): find a Payment that needs attention
// (or any Payment, by status), read its step history, and issue a full
// refund or retry a failed or stuck one through sb-express-app's
// POST /admin/payments/:payment_id/refund (spec 34). One call acts on one
// refund target, and the page always names it. Which action a call takes,
// and whether any is allowed, is the backend's decision: its 400 and 409
// reasons are shown as they come. Not the full admin payments page.

const COPY = {
  title: 'Payment Refunds',
  subtitle: 'Issue a full refund, or retry a failed or stuck one. Refunds are always full.',
  filterLabel: 'Show',
  loading: 'Loading payments...',
  emptyAttention: 'No payments need attention.',
  emptyFiltered: 'No payments match this filter.',
  loadFailed: 'Failed to load payments.',
  forbidden: 'You do not have permission to manage payments.',
  retry: 'Retry',
  previous: 'Previous',
  next: 'Next',
  view: 'View',
  detailsTitle: 'Payment',
  historyTitle: 'Step history',
  historyLoading: 'Loading step history...',
  historyEmpty: 'No steps recorded yet.',
  historyFailed: 'Failed to load the step history.',
  targetsTitle: 'Refund targets',
  targetsHint: 'This payment has more than one Razorpay payment. Choose the one to refund or retry.',
  noTargets: 'Nothing to refund: this payment has not been paid.',
  refundAction: 'Refund or retry',
  confirm: 'Confirm',
  cancel: 'Cancel',
  sending: 'Sending...',
  confirmPrompt: (target: string, amount: string) =>
    `Send a full refund, or retry the refund, of ${amount} for ${target}?`,
  badRequest: 'Refund not sent:',
  conflict: 'Nothing to refund or retry:',
  notFound: 'Payment not found. It may have been removed; reload the list.',
  razorpayUnavailable: 'Razorpay could not be reached, so no new refund was attempted. Try again later.',
  refundFailed: 'Failed to send the refund. Please try again.',
  refundAccepted: 'Refund request accepted.',
  filterNeedsAttention: 'Needs attention',
  filterAll: 'All payments',
  attentionFailed: 'Failed refund',
  attentionStuck: 'Stuck refund',
  noAttention: '—',
  paymentColumns: ['Payment', 'Appointment', 'Amount', 'Status', 'Attention', 'Paid at', ''],
  historyColumns: ['Time', 'Step', 'Outcome', 'Source', 'Attempt', 'Razorpay payment', 'Error'],
};

const OUTCOME_COPY: Record<AdminRefundOutcome, string> = {
  initiated: 'Refund sent to Razorpay. It will show as refunded once Razorpay processes it.',
  unknown: 'Razorpay did not answer. If the refund does not move, it will show as stuck and can be retried.',
  failed: 'Razorpay rejected the refund. It is marked refund failed and can be retried.',
  adopted: 'An existing Razorpay refund was found for this attempt and recorded.',
};

const STATUS_LABELS: Record<PaymentStatus, string> = {
  created: 'Awaiting payment',
  paid: 'Paid',
  expired: 'Expired',
  cancelled_unpaid: 'Cancelled (unpaid)',
  refund_pending: 'Refund pending',
  refunded: 'Refunded',
  refund_failed: 'Refund failed',
};

const TARGET_STATE_LABELS: Record<RefundTargetState, string> = {
  none: 'No refund yet',
  pending: 'Refund pending',
  processed: 'Refunded',
  failed: 'Refund failed',
  stuck: 'Refund stuck, can be retried',
};

// The default filter is 'needs_attention': only Payments with a failed or
// stuck refund.
type Filter = 'needs_attention' | PaymentStatus | 'all';

const FILTER_OPTIONS: { value: Filter; label: string }[] = [
  { value: 'needs_attention', label: COPY.filterNeedsAttention },
  { value: 'paid', label: STATUS_LABELS.paid },
  { value: 'refund_failed', label: STATUS_LABELS.refund_failed },
  { value: 'refund_pending', label: STATUS_LABELS.refund_pending },
  { value: 'refunded', label: STATUS_LABELS.refunded },
  { value: 'all', label: COPY.filterAll },
];

const PAGE_SIZE = 50;

const queryFor = (filter: Filter, page: number): AdminPaymentListQuery => {
  const base = { page, limit: PAGE_SIZE };
  if (filter === 'needs_attention') return { ...base, needs_attention: true };
  if (filter === 'all') return base;
  return { ...base, status: filter };
};

// Errors arrive already normalized into an ApiError by api.ts's response
// interceptor (a network failure becomes a generic status-500 ApiError).
const statusAndMessage = (error: unknown): Partial<ApiError> =>
  typeof error === 'object' && error !== null ? (error as Partial<ApiError>) : {};

const describeLoadError = (error: unknown, fallback: string): string =>
  statusAndMessage(error).status === 403 ? COPY.forbidden : fallback;

// 400 and 409 carry the backend's specific reason (e.g. "Cancel the
// appointment first..."), so they are shown verbatim behind a fixed lead.
const describeRefundError = (error: unknown): string => {
  const { status, message } = statusAndMessage(error);
  const reason = typeof message === 'string' ? message : '';
  if (status === 400) return `${COPY.badRequest} ${reason}`.trim();
  if (status === 409) return `${COPY.conflict} ${reason}`.trim();
  if (status === 403) return COPY.forbidden;
  if (status === 404) return COPY.notFound;
  if (status === 502 || status === 503) return COPY.razorpayUnavailable;
  return COPY.refundFailed;
};

const formatDateTime = (value?: string | null): string => (value ? new Date(value).toLocaleString() : '—');

const attentionLabel = (payment: AdminPayment): string => {
  const flags: string[] = [];
  if (payment.attention?.failed) flags.push(COPY.attentionFailed);
  if (payment.attention?.stuck) flags.push(COPY.attentionStuck);
  return flags.join(', ') || COPY.noAttention;
};

const StepHistory: React.FC<{ paymentId: string; reloadKey: number }> = ({ paymentId, reloadKey }) => {
  const [steps, setSteps] = useState<PaymentTransaction[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setSteps(null);
    setError(null);
    apiService
      .getPaymentTransactions(paymentId)
      .then((history) => {
        if (!cancelled) setSteps(history.transactions ?? []);
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(describeLoadError(e, COPY.historyFailed));
      });
    return () => {
      cancelled = true;
    };
  }, [paymentId, reloadKey]);

  if (error) {
    return (
      <div className="error mb-4" role="alert">
        {error}
      </div>
    );
  }
  if (!steps) return <div className="loading">{COPY.historyLoading}</div>;
  if (steps.length === 0) return <p className="text-gray-600">{COPY.historyEmpty}</p>;

  return (
    <div className="table-container">
      <table className="table" aria-label={COPY.historyTitle}>
        <thead>
          <tr>
            {COPY.historyColumns.map((column) => (
              <th key={column}>{column}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {steps.map((step, index) => (
            <tr key={`${step.createdAt}:${step.type}:${index}`}>
              <td>{formatDateTime(step.createdAt)}</td>
              <td>{step.type}</td>
              <td>{step.outcome}</td>
              <td>{step.source}</td>
              <td>{step.attempt ?? '—'}</td>
              <td>{step.razorpay_payment_id ?? '—'}</td>
              <td>
                {step.error_code ?? '—'}
                {step.error_message && <div className="text-gray-600">{step.error_message}</div>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

interface RefundActionProps {
  payment: AdminPayment;
  onSending: () => void;
  onRefunded: (result: AdminRefundResult) => void;
}

const RefundAction: React.FC<RefundActionProps> = ({ payment, onSending, onRefunded }) => {
  const targets: RefundTarget[] = refundTargets(payment, new Date());
  // With one target it is the only choice; with several the admin must pick.
  const [chosen, setChosen] = useState<string | null>(
    targets.length === 1 ? targets[0].razorpay_payment_id : null,
  );
  const [confirming, setConfirming] = useState<boolean>(false);
  const [sending, setSending] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  if (targets.length === 0) return <p className="text-gray-600">{COPY.noTargets}</p>;

  const send = async (target: string) => {
    try {
      setSending(true);
      setError(null);
      onSending();
      const result = await apiService.refundPayment(payment.payment_id, target);
      setConfirming(false);
      onRefunded(result);
    } catch (e: unknown) {
      setError(describeRefundError(e));
      setConfirming(false);
    } finally {
      setSending(false);
    }
  };

  return (
    <div>
      {error && (
        <div className="error mb-4" role="alert">
          {error}
        </div>
      )}

      {targets.length > 1 && <p className="text-gray-600 mb-4">{COPY.targetsHint}</p>}

      <fieldset className="mb-4" aria-label={COPY.targetsTitle}>
        {targets.map((target) => (
          <label key={target.razorpay_payment_id} className="form-label" style={{ display: 'block' }}>
            <input
              type="radio"
              name={`refund-target-${payment.payment_id}`}
              value={target.razorpay_payment_id}
              checked={chosen === target.razorpay_payment_id}
              onChange={() => {
                setChosen(target.razorpay_payment_id);
                setConfirming(false);
              }}
              disabled={sending}
            />{' '}
            {target.razorpay_payment_id} ({TARGET_STATE_LABELS[target.state]})
          </label>
        ))}
      </fieldset>

      {!confirming && (
        <button className="btn btn-danger" disabled={!chosen || sending} onClick={() => setConfirming(true)}>
          {COPY.refundAction}
        </button>
      )}

      {confirming && chosen && (
        <div>
          <p className="mb-4">{COPY.confirmPrompt(chosen, formatPaise(payment.amount))}</p>
          <button className="btn btn-danger" disabled={sending} onClick={() => send(chosen)}>
            {sending ? COPY.sending : COPY.confirm}
          </button>{' '}
          <button className="btn btn-secondary" disabled={sending} onClick={() => setConfirming(false)}>
            {COPY.cancel}
          </button>
        </div>
      )}
    </div>
  );
};

const PaymentRefundsPage: React.FC = () => {
  const [filter, setFilter] = useState<Filter>('needs_attention');
  const [page, setPage] = useState<number>(1);
  const [payments, setPayments] = useState<AdminPayment[] | null>(null);
  const [total, setTotal] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  // Kept apart from the list: after a refund the Payment may no longer match
  // the filter, and its details should stay open.
  const [selected, setSelected] = useState<AdminPayment | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  // Bumped after a refund so the step history re-reads and the refund
  // action resets its choice and confirmation.
  const [refreshCount, setRefreshCount] = useState<number>(0);
  // Only the latest list request may update the page, so a slow response to
  // an earlier filter or page never overwrites a newer one.
  const latestListRequest = useRef<number>(0);

  const loadPayments = useCallback(async () => {
    const request = ++latestListRequest.current;
    const isLatest = () => request === latestListRequest.current;
    try {
      setLoading(true);
      setLoadError(null);
      const list = await apiService.listAdminPayments(queryFor(filter, page));
      if (!isLatest()) return;
      const listed = list.payments ?? [];
      const listedTotal = list.total ?? 0;
      // A refund can empty the last page; step back to the one that now is.
      const lastPage = Math.max(1, Math.ceil(listedTotal / PAGE_SIZE));
      if (page > lastPage) {
        setPage(lastPage);
        return;
      }
      setPayments(listed);
      setTotal(listedTotal);
      setSelected((current) => (current && listed.find((p) => p.payment_id === current.payment_id)) || current);
    } catch (e: unknown) {
      if (isLatest()) setLoadError(describeLoadError(e, COPY.loadFailed));
    } finally {
      if (isLatest()) setLoading(false);
    }
  }, [filter, page]);

  useEffect(() => {
    loadPayments();
  }, [loadPayments]);

  const lastPage = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const handleRefunded = (result: AdminRefundResult) => {
    setSuccess(OUTCOME_COPY[result.outcome] ?? COPY.refundAccepted);
    // The backend returns the Payment as it now stands. Use it, since the
    // list reload may no longer include it (e.g. it stopped needing
    // attention); the list-only attention flags stay from the opened row.
    const refreshed = result.payment;
    if (refreshed) {
      setSelected((current) => (current ? { ...current, ...refreshed } : current));
    }
    setRefreshCount((count) => count + 1);
    loadPayments();
  };

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900">{COPY.title}</h2>
        <p className="text-gray-600">{COPY.subtitle}</p>
      </div>

      <div className="mb-4">
        <label className="form-label" htmlFor="payment-refunds-filter">
          {COPY.filterLabel}
        </label>
        <select
          id="payment-refunds-filter"
          className="form-select"
          value={filter}
          onChange={(e) => {
            setFilter(e.target.value as Filter);
            setPage(1);
            setSelected(null);
            setSuccess(null);
          }}
        >
          {FILTER_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      {success && <div className="success mb-4">{success}</div>}

      {loading && <div className="loading">{COPY.loading}</div>}

      {!loading && loadError && (
        <div className="mb-4">
          <div className="error mb-4" role="alert">
            {loadError}
          </div>
          <button className="btn btn-secondary" onClick={loadPayments}>
            {COPY.retry}
          </button>
        </div>
      )}

      {!loadError && payments && payments.length === 0 && (
        <p className="text-gray-600">{filter === 'needs_attention' ? COPY.emptyAttention : COPY.emptyFiltered}</p>
      )}

      {!loadError && payments && payments.length > 0 && (
        <div className="card mb-6">
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  {COPY.paymentColumns.map((column) => (
                    <th key={column}>{column}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {payments.map((payment) => (
                  <tr key={payment.payment_id}>
                    <td>{payment.payment_id}</td>
                    <td>{payment.appointment_id}</td>
                    <td>{formatPaise(payment.amount)}</td>
                    <td>{STATUS_LABELS[payment.status] ?? payment.status}</td>
                    <td>{attentionLabel(payment)}</td>
                    <td>{formatDateTime(payment.paid_at)}</td>
                    <td>
                      <button
                        className="btn btn-secondary"
                        aria-label={`${COPY.view} ${payment.payment_id}`}
                        onClick={() => {
                          setSelected(payment);
                          setSuccess(null);
                        }}
                      >
                        {COPY.view}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {lastPage > 1 && (
            <div className="card-content">
              <button className="btn btn-secondary" disabled={page <= 1} onClick={() => setPage(page - 1)}>
                {COPY.previous}
              </button>{' '}
              <span className="text-gray-600">
                {page} / {lastPage}
              </span>{' '}
              <button className="btn btn-secondary" disabled={page >= lastPage} onClick={() => setPage(page + 1)}>
                {COPY.next}
              </button>
            </div>
          )}
        </div>
      )}

      {selected && (
        <section className="card mb-6" aria-label={`${COPY.detailsTitle} ${selected.payment_id}`}>
          <div className="card-header">
            <h3 className="card-title">
              {COPY.detailsTitle} {selected.payment_id}
            </h3>
          </div>
          <div className="card-content">
            <h4 className="font-bold mb-4">{COPY.targetsTitle}</h4>
            {/* Keyed so a refresh after a refund resets the choice and confirmation. */}
            <RefundAction
              key={`${selected.payment_id}:${refreshCount}`}
              payment={selected}
              onSending={() => setSuccess(null)}
              onRefunded={handleRefunded}
            />

            <h4 className="font-bold mb-4 mt-6">{COPY.historyTitle}</h4>
            <StepHistory paymentId={selected.payment_id} reloadKey={refreshCount} />
          </div>
        </section>
      )}
    </div>
  );
};

export default PaymentRefundsPage;
